import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    Box,
    Typography,
} from '@mui/material';
import Link from 'next/link';

import CustomImageComponent from '@/components/shared/custom/custom-image';
import { Rutas } from '@/constants/rutas';
import { translate } from '@/data/fixture-transalations';
import { getMasterPhotoById, getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage, getCompetitionName } from '@/helpers/leagues-helper';
import CustomLink from '@/components/shared/custom/custom-link';

const JornadasTabTable = ({ style, data, selectedLeague, statType }) => {
    const [orderBy, setOrderBy] = useState('name');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [orderByMatch, setOrderByMatch] = useState(null);

    const handleSort = (column, matchId = null) => {
        const isAsc = orderBy === column && orderDirection === 'asc' && orderByMatch === matchId;
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
        setOrderByMatch(matchId);
    };

    const partidos = data.partidos
        .filter((partido) => selectedLeague === 'all' || partido.leagueId === selectedLeague)
        .sort((a, b) => new Date(b.fixture.date) - new Date(a.fixture.date));

    const getPlayerStat = (playerData, statType) => {
        let statValue = 0;
        switch (statType) {
            case 'minutos':
                statValue = playerData.statistics[0]?.games?.minutes || 0;
                break;
            case 'goles':
                statValue = playerData.statistics[0]?.goals?.total || 0;
                break;
            case 'asistencias':
                statValue = playerData.statistics[0]?.goals?.assists || 0;
                break;
            case 'tarjetas amarillas':
                statValue = playerData.statistics[0]?.cards?.yellow || 0;
                break;
            case 'tarjetas rojas':
                statValue = playerData.statistics[0]?.cards?.red || 0;
                break;
            case 'faltas cometidas':
                statValue = playerData.statistics[0]?.fouls?.committed || 0;
                break;
            case 'faltas recibidas':
                statValue = playerData.statistics[0]?.fouls?.drawn || 0;
                break;
            case 'penales fallados':
                statValue = playerData.statistics[0]?.penalty?.missed || 0;
                break;
            case 'penales anotados':
                statValue = playerData.statistics[0]?.penalty?.scored || 0;
                break;
            case 'goles en propia puerta':
                statValue = playerData.statistics[0]?.goals?.owngoals || 0;
                break;
            case 'paradas':
                statValue = playerData.statistics[0]?.goals?.conceded || 0;
                break;
            case 'penales parados':
                statValue = playerData.statistics[0]?.penalty?.saved || 0;
                break;
            case 'disparos a puerta':
                statValue = playerData.statistics[0]?.shots?.total || 0;
                break;
            case 'disparos entre los tres palos':
                statValue = playerData.statistics[0]?.shots?.on || 0;
                break;
            default:
                statValue = 0;
        }
        return statValue === 0 ? -1 : Number(statValue);
    };

    const sortedPlayers = data?.partidos?.[0]?.stats?.[0]?.players?.slice()?.sort((a, b) => {
        if (orderByMatch) {
            const aStat = data.partidos
                .find((p) => p.fixture.id === orderByMatch)
                ?.stats[0]?.players.find((p) => p.player.id === a.player.id);
            const bStat = data.partidos
                .find((p) => p.fixture.id === orderByMatch)
                ?.stats[0]?.players.find((p) => p.player.id === b.player.id);
            const aValue = aStat ? getPlayerStat(aStat, statType) : 0;
            const bValue = bStat ? getPlayerStat(bStat, statType) : 0;
            return orderDirection === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
            if (orderBy === 'name') {
                return orderDirection === 'asc'
                    ? a.player.name.localeCompare(b.player.name)
                    : b.player.name.localeCompare(a.player.name);
            } else {
                const aStat = getPlayerStat(a, statType);
                const bStat = getPlayerStat(b, statType);
                return orderDirection === 'asc' ? aStat - bStat : bStat - aStat;
            }
        }
    });

    const getPlayerCellMinutes = (playerData, partido) => {
        let maxMinutesInMatch = partido?.stats?.[0]?.players?.reduce(
            (acc, p) => (p.statistics[0]?.games?.minutes > acc ? p.statistics[0]?.games?.minutes : acc),
            0
        );

        let minutesPlayed = playerData.statistics[0]?.games?.minutes ?? 0;
        if (minutesPlayed === 0) {
            return '-';
        }

        const isStarter =
            partido.home.startXI.some((p) => p.player.id === playerData.player.id) ||
            partido.away.startXI.some((p) => p.player.id === playerData.player.id);

        let icon = null;

        if (isStarter) {
            if (minutesPlayed < maxMinutesInMatch) {
                icon = <ArrowDownwardIcon style={{ color: '#d32f2f', fontSize: '14px' }} />;
            }
        } else {
            icon = <ArrowUpwardIcon style={{ color: '#01aa4d', fontSize: '14px' }} />;
        }

        return (
            <Box display="flex" alignItems="flex-end" justifyContent="center">
                {minutesPlayed} {icon}
            </Box>
        );
    };

    const getPlayerCell = (playerData, partido) => {
        switch (statType) {
            case 'minutos':
                return getPlayerCellMinutes(playerData, partido);
            case 'goles':
            case 'asistencias':
            case 'tarjetas amarillas':
            case 'tarjetas rojas':
            case 'faltas cometidas':
            case 'faltas recibidas':
            case 'penales fallados':
            case 'penales anotados':
            case 'goles en propia puerta':
            case 'paradas':
            case 'penales parados':
            case 'disparos a puerta':
            case 'disparos entre los tres palos':
                return getPlayerStat(playerData, statType) > 0 ? getPlayerStat(playerData, statType) : '-';
            default:
                return '-';
        }
    };

    return (
        <TableContainer component={Paper} style={{ ...style }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                width: '200px',
                                minWidth: '200px',
                                position: 'sticky',
                                left: 0,
                                zIndex: 5,
                                textAlign: 'center',
                                backgroundColor: 'background.card',
                            }}
                        >
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? orderDirection : 'asc'}
                                onClick={() => handleSort('name')}
                            >
                                <Typography sx={{ fontSize: 14 }}>Nombre</Typography>
                            </TableSortLabel>
                        </TableCell>
                        {partidos.map((partido, index) => (
                            <TableCell key={index} style={{ width: '100px', minWidth: '100px' }}>
                                <TableSortLabel
                                    active={orderBy === 'stat' && orderByMatch === partido.fixture.id}
                                    direction={
                                        orderBy === 'stat' && orderByMatch === partido.fixture.id
                                            ? orderDirection
                                            : 'asc'
                                    }
                                    onClick={() => handleSort('stat', partido.fixture.id)}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex' }}>
                                            <CustomImageComponent
                                                src={getTeamImageById(partido?.home?.team?.id)}
                                                width={20}
                                                height={20}
                                                alt={partido?.home?.team?.name}
                                            />
                                            <CustomImageComponent
                                                src={getTeamImageById(partido?.away?.team?.id)}
                                                width={20}
                                                height={20}
                                                alt={partido?.away?.team?.name}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            <CustomImageComponent
                                                src={getCompetitionImage(partido?.leagueId)}
                                                width={15}
                                                height={15}
                                                alt={getCompetitionName(partido?.leagueId)}
                                            />
                                            {translate(partido?.round, true)}
                                        </Box>
                                        <Box>
                                            <Link
                                                href={`${Rutas.PARTIDO}/${partido.fixture.id}/alineaciones-probables`}
                                                style={{
                                                    width: '100px',
                                                    minWidth: '100px',
                                                    textAlign: 'center',
                                                    fontSize: '12px',
                                                    color: '#1D6FB1 ',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                prefetch={false}
                                            >
                                                Ver Partido
                                            </Link>
                                        </Box>
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedPlayers?.map((player) => (
                        <TableRow key={player.player.id}>
                            <TableCell
                                sx={{
                                    width: '200px',
                                    minWidth: '200px',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    bgcolor: 'background.card',
                                }}
                            >
                                <CustomImageComponent
                                    src={getMasterPhotoById(player?.player?.id, 60)}
                                    width={40}
                                    height={40}
                                    alt={player?.player?.name}
                                />
                                <Typography sx={{ fontSize: 14 }}>{player?.player?.name}</Typography>
                            </TableCell>
                            {partidos?.map((partido) => {
                                const playerData = partido?.stats?.[0]?.players?.find(
                                    (p) => p.player.id === player.player.id
                                );
                                return (
                                    <TableCell
                                        key={partido.id}
                                        style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}
                                    >
                                        {playerData ? getPlayerCell(playerData, partido) : '-'}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell
                            sx={{
                                width: '200px',
                                minWidth: '200px',
                                position: 'sticky',
                                left: 0,
                                zIndex: 5,
                                bgcolor: 'background.card',
                            }}
                        />
                        {partidos.map((partido) => (
                            <TableCell
                                key={`view-${partido.id}`}
                                sx={{
                                    width: '100px',
                                    minWidth: '100px',
                                    textAlign: 'center',
                                    fontSize: '12px',
                                }}
                            >
                                <CustomLink
                                    href={`${Rutas.PARTIDO}/${partido.fixture.id}/alineaciones-probables`}
                                >
                                    <Typography sx={{ fontSize: 14, color: 'primary.main' }}>
                                        Ver Partido
                                    </Typography>
                                </CustomLink>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default JornadasTabTable;
